// src/data/schemas/UserReport.js
// Community-submitted reports — the core feed content.
// Firestore collection: reports

import { uuid, nowISO, isLat, isLon, isISO, isIn, runValidation, SEVERITY_LEVEL, toFirestoreClean, fromFirestoreNormalize } from './shared';
import { geohashForReport } from '@services/geolocation/geohash';

export const REPORT_TYPE = [
  'sos',       // life-threatening — requires immediate help
  'flood',     // flooding observed
  'road',      // road blocked / damaged
  'power',     // power outage / restoration
  'gas',       // gas station status
  'shelter',   // shelter open / closed / full
  'medical',   // medical emergency
  'fire',      // structure or grass fire
  'hazmat',    // chemical / hazardous material
  'good',      // positive update — shelter open, road clear
  'info',      // general informational
];

// SOS subtypes — granular for dispatch routing
export const SOS_SUBTYPE = ['medical', 'trapped', 'food_water', 'welfare_check', 'generator', 'other'];

export const SCHEMA_USER_REPORT = {
  id:               { required: true,  type: 'string' },
  type:             { required: true,  type: 'string',  check: (v) => isIn(v, REPORT_TYPE),   message: `one of: ${REPORT_TYPE.join(', ')}` },
  severity:         { required: true,  type: 'number',  check: (v) => isIn(v, SEVERITY_LEVEL),message: '1 (info) to 5 (life-threatening)' },
  body:             { required: true,  type: 'string',  maxLen: 1000 },
  // Author
  authorId:         { required: false, type: 'string' },
  authorDisplay:    { required: true,  type: 'string',  maxLen: 60 },
  // Location
  lat:              { required: false, type: 'number',  check: isLat, message: 'valid latitude' },
  lon:              { required: false, type: 'number',  check: isLon, message: 'valid longitude' },
  locationAccuracyM:{ required: false, type: 'number',  min: 0 },
  address:          { required: false, type: 'string',  maxLen: 300 },
  zone:             { required: false, type: 'string',  maxLen: 100 },
  parish:           { required: false, type: 'string',  maxLen: 100 },
  // Media
  mediaUrls:        { required: false, type: 'array'  },   // Firebase Storage URLs
  // Timestamps
  createdAt:        { required: true,  type: 'string',  check: isISO, message: 'valid ISO datetime' },
  updatedAt:        { required: false, type: 'string',  check: isISO },
  // Moderation / confidence
  confirmCount:     { required: false, type: 'number',  min: 0 },
  flagCount:        { required: false, type: 'number',  min: 0 },
  isHidden:         { required: false, type: 'boolean' },
  // SOS extensions (type === 'sos' only)
  sosSubtype:       { required: false, type: 'string',  check: (v) => isIn(v, SOS_SUBTYPE) },
  sosUrgency:       { required: false, type: 'string'  },  // 'life_safety' | 'urgent' | 'assistance'
  sosStatus:        { required: false, type: 'string'  },  // 'needs_help' | 'assistance_reported' | 'resolved'
  responseStatus:   { required: false, type: 'string'  },  // 'unknown' | 'community_responding' | 'services_notified' | 'resolved'
  sosResolved:      { required: false, type: 'boolean' },
  sosResolvedAt:    { required: false, type: 'string',  check: isISO },
  // Geohash — derived from lat/lon, used for Firestore geo queries
  geohash:          { required: false, type: 'string'  },

  // Offline queue fields — stripped before Firestore write
  _queued:          { required: false, type: 'boolean' },
  _queuedAt:        { required: false, type: 'number'  },
  // Source tag
  source:           { required: false, type: 'string'  },  // 'app' | 'nws_injected' | 'seed'
};

// Auto-derive severity from type when not explicitly set
const TYPE_SEVERITY = {
  sos:     5,
  medical: 4,
  flood:   4,
  fire:    4,
  hazmat:  4,
  road:    3,
  power:   3,
  shelter: 2,
  gas:     2,
  info:    1,
  good:    1,
};

export function createUserReport(overrides = {}) {
  const type = overrides.type ?? 'info';
  return {
    id:                uuid(),
    type,
    severity:          TYPE_SEVERITY[type] ?? 1,
    body:              '',
    authorId:          null,
    uid:               null,  // same as authorId — required by Firestore rules
    authorDisplay:     'Anonymous',
    lat:               null,
    lon:               null,
    locationAccuracyM: null,
    address:           null,
    zone:              null,
    parish:            null,
    mediaUrls:         [],
    createdAt:         nowISO(),
    updatedAt:         null,
    confirmCount:      0,
    flagCount:         0,
    isHidden:          false,
    sosSubtype:        null,
    sosUrgency:        null,
    sosStatus:         'needs_help',
    responseStatus:    'unknown',
    sosResolved:       null,
    sosResolvedAt:     null,
    source:            'app',
    sourceType:        'community',  // ensures feed routing works
    ts:                nowISO(),     // Firestore query field — ISO string to match useZoneFeed cutoff
    // Derive geohash from coordinates if provided
    geohash:           null,
    ...overrides,
    // severity always derived from type unless caller explicitly overrides it
    severity: overrides.severity ?? TYPE_SEVERITY[overrides.type ?? 'info'] ?? 1,
    // geohash computed from final lat/lon
    geohash: overrides.geohash ?? (overrides.lat && overrides.lon ? geohashForReport(overrides.lat, overrides.lon) : null),
  };
}

export function validateUserReport(obj) {
  const result = runValidation(obj, SCHEMA_USER_REPORT);
  // SOS must have location (lat+lon or address)
  if (obj.type === 'sos' && !obj.lat && !obj.address) {
    result.errors.push('location: SOS reports must include lat+lon or address');
    result.valid = false;
  }
  return result;
}

// Strip offline-queue tracking before Firestore write
export function reportForFirestore(report) {
  // eslint-disable-next-line no-unused-vars
  const { _queued, _queuedAt, ...clean } = report;
  return { ...clean, updatedAt: nowISO() };
}

// Build from SOS form submission
export function reportFromSOSForm({ sosSubtype, sosUrgency, body, lat, lon, locationAccuracyM, address, zone, mediaUrls = [] }) {
  return createUserReport({
    type:              'sos',
    body,
    sosSubtype:        sosSubtype ?? 'other',
    sosUrgency:        sosUrgency ?? 'urgent',
    lat:               lat ?? null,
    lon:               lon ?? null,
    locationAccuracyM: locationAccuracyM ?? null,
    address:           address ?? null,
    zone:              zone ?? null,
    mediaUrls,
    source:            'app',
  });
}

// Seed posts that match UserReport schema (used in store/index.js)
export const SEED_REPORTS = []; // Seed data removed — use real community reports

const USER_REPORT_FS_KEYS = [
  'id','type','severity','body',
  'authorId','authorDisplay',
  'lat','lon','locationAccuracyM','address','zone','parish','deviceId',
  'mediaUrls','createdAt','updatedAt',
  'confirmCount','flagCount','isHidden',
  'sosSubtype','sosUrgency','sosStatus','responseStatus','sosResolved','sosResolvedAt','source','sourceType','ts','geohash',
];

const USER_REPORT_MIGRATIONS = {
  // v0: 'likes' was used instead of 'confirmCount'
  0: (d) => {
    if (d.likes !== undefined && d.confirmCount === undefined) {
      d.confirmCount = d.likes;
      delete d.likes;
    }
    if (d.flags !== undefined && d.flagCount === undefined) {
      d.flagCount = d.flags;
      delete d.flags;
    }
    // v0: 'auth' was used instead of 'authorDisplay'
    if (d.auth !== undefined && !d.authorDisplay) {
      d.authorDisplay = d.auth;
      delete d.auth;
    }
    // v0: 'ts' was used instead of 'createdAt' (ms → ISO)
    if (typeof d.ts === 'number' && !d.createdAt) {
      d.createdAt = new Date(d.ts).toISOString();
      delete d.ts;
    }
    return d;
  },
};

export const UserReportConverter = {
  toFirestore:   (r) => toFirestoreClean(r, USER_REPORT_FS_KEYS),
  fromFirestore: (snap) => fromFirestoreNormalize(snap.data(), createUserReport, USER_REPORT_MIGRATIONS),
};
